import { VehicleSavingsCalculatorV2 } from '@/catalog/types';
import Spacer from '@/common/components/spacer';
import FlexcarWordmark2024NoCircle from '@/icons/flexcar-wordmark-2024-no-circle';
import PersonalizedSavings from '@/icons/personalized-savings';
import { getDynamicCurrency } from '@/util-conversion/currency';
import CarSalesProceeds from './car-sales-proceeds';
import SavingsCalculatorBlobContainer from './savings-calculator-blob-container';
import SavingsCostComparisonTable from './savings-cost-comparison-table';

interface SavingsCostComparisonProps {
  savings: VehicleSavingsCalculatorV2;
  ownershipYears: number;
  membershipCost?: string;
}

export default function SavingsCostComparison({
  savings,
  ownershipYears,
  membershipCost,
}: SavingsCostComparisonProps) {
  return (
    <>
      <SavingsCalculatorBlobContainer className="px-0 py-[16px]">
        <div className="flex flex-col px-[16px]">
          <div className="flex flex-row gap-[8px]">
            <PersonalizedSavings className="h-[22px] w-[23px]" />
            <h3>Cost Comparison</h3>
          </div>

          <Spacer variant="height16" />

          <SavingsCostComparisonTable
            buyingUpfront={savings.upfront_buying_cost}
            buyingMonthlyCar={savings.monthly_car_payment}
            buyingMonthlyInsurance={savings.monthly_insurance}
            flexcarUpfront={savings.upfront_flexcar_cost}
            flexcarMonthlyCar={savings.flexcar_year_one_and_two_pre_tax}
            flexcarMonthlyInsurance={savings.flexcar_monthly_insurance_pre_tax}
            flexcarShippingCost={savings.flexcar_shipping_fee_pre_tax}
            flexcarNewCarSurcharge={savings.flexcar_new_vehicle_fee_pre_tax}
            flexcarMembershipCost={Number(membershipCost)}
          />
        </div>

        <Spacer variant="height16" />

        <div className="-ml-[1px] -mr-[1px] rounded-[16px] bg-flexcar-blue30 px-[17px] py-[12px]">
          <div className="flex flex-row justify-between">
            <div className="flex flex-row items-center gap-[5px]">
              <FlexcarWordmark2024NoCircle className="h-[15px] w-[77px]" />
              <p className="body-strong text-flexcar-aqua50">
                {`${ownershipYears}-year savings`}:
              </p>
            </div>
            <p className="body-strong text-flexcar-aqua50">{`${getDynamicCurrency(savings.flexcar_savings_over_ownership_year_term)}`}</p>
          </div>
        </div>

        <Spacer variant="height12" />

        <div className="flex flex-col px-[16px]">
          <CarSalesProceeds
            tradeInValue={savings.ownership_year_term_trade_in_value}
            carSalesProceeds={savings.ownership_year_car_sales_proceeds}
            remainingLoanBalance={savings.ownership_year_remaining_loan_balance}
            ownershipYearsToDate={savings.ownership_years_to_date}
            ownershipYears={ownershipYears}
          />

          <Spacer variant="height8" />

          <div className="flex items-baseline justify-between">
            <p className="body2-strong">Flexcar savings after car sale:</p>
            <p className="body2-strong">
              {getDynamicCurrency(
                savings.flexcar_savings_after_car_sale_over_ownership_year_term
              )}
            </p>
          </div>
        </div>
      </SavingsCalculatorBlobContainer>
    </>
  );
}
